import { createColumnHelper } from '@tanstack/react-table';
import {
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { ReportMetricsResponse } from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { defaultColumnHelperAccessor } from 'src/tables/ReportTableCommon/DefaultColumnHelperAccessor';
import { LinkCell } from 'src/tables/ReportTableCommon/LinkCell';
import { reportV2AccessorFn } from 'src/tables/ReportTableCommon/ReportTableCommon.utils';
import { TextCell } from 'src/tables/ReportTableCommon/TextCell';
import { ReportColumnPersonalizationFlags } from 'src/utils/columns/columnUtils.types';
import {
  TicketGroupReportTableGroupingColumnId,
  TicketGroupReportTableValueColumnId,
} from 'src/utils/columns/ticketGroup/ticketGroupColumnUtils.types';
import {
  TICKET_GROUP_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID,
  TICKET_GROUP_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID,
} from 'src/utils/constants/contentIdMaps';
import {
  createPurchaseEventPageUrl,
  getPurchaseOrderRelativeUrl,
} from 'src/utils/purchaseUtils';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import { ReportValueType } from 'src/WebApiController';

const columnHelper = createColumnHelper<ReportMetricsResponse | null>();

export const TICKET_GROUP_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG = {
  [TicketGroupReportTableGroupingColumnId.PurchaseOrderId]:
    columnHelper.accessor(
      reportV2AccessorFn(
        TicketGroupReportTableGroupingColumnId.PurchaseOrderId
      ),
      {
        id: TicketGroupReportTableGroupingColumnId.PurchaseOrderId,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                TICKET_GROUP_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  TicketGroupReportTableGroupingColumnId.PurchaseOrderId
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: ({ getValue }) => {
          const id = getValue<string | null>();

          if (!id) {
            return <></>;
          }

          return (
            <LinkCell
              text={`#${id}`}
              url={getPurchaseOrderRelativeUrl(id)}
              separator={<span>{', '}</span>}
              openInNewTab
              align="left"
              style={{ fontWeight: 600 }}
            />
          );
        },
      }
    ),
  [TicketGroupReportTableGroupingColumnId.TicketGroupId]: columnHelper.accessor(
    reportV2AccessorFn(TicketGroupReportTableGroupingColumnId.TicketGroupId),
    {
      id: TicketGroupReportTableGroupingColumnId.TicketGroupId,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              TICKET_GROUP_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                TicketGroupReportTableGroupingColumnId.TicketGroupId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: ({ getValue }) => {
        const id = getValue<string | null>();

        if (!id) {
          return <></>;
        }

        return (
          <TableCellDiv align="left" style={{ fontWeight: 600 }}>
            #{id}
          </TableCellDiv>
        );
      },
    }
  ),
  [TicketGroupReportTableGroupingColumnId.EventId]: columnHelper.accessor(
    reportV2AccessorFn(TicketGroupReportTableGroupingColumnId.EventId),
    {
      id: TicketGroupReportTableGroupingColumnId.EventId,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              TICKET_GROUP_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                TicketGroupReportTableGroupingColumnId.EventId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: (params) => {
        const eventId = params.getValue<string | null | undefined>();
        if (!eventId) {
          return <TextCell {...params} align={'left'} />;
        }
        const eventUrl = createPurchaseEventPageUrl(eventId);
        return (
          <LinkCell
            text={String(params.getValue())}
            url={eventUrl}
            align="left"
          />
        );
      },
    }
  ),
} as const;

export const TicketGroupReportTableColumnDef = (
  columnId: string,
  columnValue: string,
  valueType: ReportValueType | undefined,
  personalizationFlags: Partial<ReportColumnPersonalizationFlags> | undefined
) => {
  const specialConfig = Object.entries(
    TICKET_GROUP_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG
  ).find(([key]) => key === columnId)?.[1];

  if (specialConfig) {
    return specialConfig;
  }

  const headerContentId =
    TICKET_GROUP_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
      columnId as TicketGroupReportTableGroupingColumnId
    ] ||
    TICKET_GROUP_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID[
      columnId as TicketGroupReportTableValueColumnId
    ];

  return defaultColumnHelperAccessor(
    columnHelper,
    columnId,
    columnValue,
    headerContentId,
    ReportTypesV2.TicketGroup,
    valueType,
    personalizationFlags
  );
};
